import {
	type Organization,
	OrganizationStatusCode,
} from "@/services/organization";
import { ORGANIZATION_USER_STATUS } from "@/services/user/user.types";
import { isPlaceholderOrgId } from "@/utils/placeholder-org-id";

export { isPlaceholderOrgId };

export interface ResolveConcreteOrgIdInput {
	urlOrgId?: string | null;
	storeOrgId?: string | null;
	activeOrganizationId?: string | null;
	userInfoCurrentOrganizationId?: string | null;
	firstAvailableOrgId?: string | null;
	availableOrganizations: ReadonlyArray<{ id: string }>;
}

/**
 * Resolves the best concrete organization id. When an organization list is
 * available, candidates must belong to it; an empty list allows a persisted
 * concrete id to survive until the authoritative list loads.
 */
export function resolveConcreteOrgId(
	input: ResolveConcreteOrgIdInput,
): string | undefined {
	const candidates = [
		input.urlOrgId,
		input.storeOrgId,
		input.activeOrganizationId,
		input.userInfoCurrentOrganizationId,
		input.firstAvailableOrgId,
	];

	return candidates.find((candidate): candidate is string => {
		if (isPlaceholderOrgId(candidate)) return false;
		return (
			input.availableOrganizations.length === 0 ||
			input.availableOrganizations.some((org) => org.id === candidate)
		);
	});
}

/**
 * Helpers for deciding when the web-app shell may redirect away from a
 * deep-linked `/web/$orgId/...` URL because the org segment is invalid.
 *
 * Callers must wait until the org list is authoritative (userInfo +
 * ViewAllOrganizations, and allOrgs for platform admins) before treating a
 * missing org as invalid — otherwise platform admins bounce to the first
 * membership org during the load gap.
 */

export interface WebOrgListReadyInput {
	userInfoSuccess: boolean;
	viewAllOrganizationsFetched: boolean;
	isPlatformAdmin: boolean;
	allOrgsFetched: boolean;
}

/**
 * Returns whether the web shell has enough org-list data to validate URL orgId.
 */
export function isWebOrgListReady(input: WebOrgListReadyInput): boolean {
	if (!input.userInfoSuccess || !input.viewAllOrganizationsFetched) {
		return false;
	}
	if (input.isPlatformAdmin && !input.allOrgsFetched) {
		return false;
	}
	return true;
}

export interface ShouldRedirectInvalidWebOrgInput {
	isOrgListReady: boolean;
	firstOrgId: string | undefined;
	urlOrgIdInvalid: boolean;
}

/**
 * Returns whether the web shell should navigate to `/web/{firstOrgId}/home`.
 */
export function shouldRedirectInvalidWebOrg(
	input: ShouldRedirectInvalidWebOrgInput,
): boolean {
	return Boolean(
		input.isOrgListReady && input.firstOrgId && input.urlOrgIdInvalid,
	);
}

/**
 * Returns whether the URL orgId is missing, placeholder, or not in the list.
 */
export function isWebUrlOrgIdInvalid(
	currentOrgId: string | undefined,
	availableOrganizations: ReadonlyArray<{ id: string }>,
): boolean {
	return (
		!currentOrgId ||
		currentOrgId === "unknown" ||
		!availableOrganizations.some((org) => org.id === currentOrgId)
	);
}

export interface ResolveWebAvailableOrganizationsInput<
	T extends { statusCode?: number },
> {
	isPlatformAdmin: boolean;
	allOrgs: T[] | undefined;
	membershipOrganizations: T[];
}

/**
 * Memberships the user can use. user-info includes suspended and archived
 * memberships; only Active (status code 1) belongs in the organization list.
 */
export function selectActiveOrganizationUserMemberships<
	T extends { status?: { code?: number | null } | null },
>(memberships: readonly T[]): T[] {
	return memberships.filter(
		(membership) => membership.status?.code === ORGANIZATION_USER_STATUS.ACTIVE,
	);
}

/**
 * Organizations the user can SSO into. A membership can be Active while the
 * organization itself is still Created or Suspended.
 */
export function selectActiveMembershipOrganizations<
	T extends { statusCode?: number },
>(organizations: T[]): T[] {
	return organizations.filter(
		(org) => org.statusCode === OrganizationStatusCode.Active,
	);
}

/**
 * Admin-portal switcher memberships for a user without ViewAllOrganizations.
 * user-info sets canViewOrganizationDetails per organization.
 */
export function selectCanViewOrganizationDetailsMemberships<
	T extends { canViewOrganizationDetails?: boolean | null },
>(memberships: readonly T[]): T[] {
	return memberships.filter(
		(membership) => membership.canViewOrganizationDetails === true,
	);
}

/**
 * Active memberships that can view organization details — the list used for
 * org-admin switcher, redirects, and $orgId access (unless ViewAllOrganizations).
 */
export function selectActiveCanViewOrganizationDetailsMemberships<
	T extends {
		status?: { code?: number | null } | null;
		canViewOrganizationDetails?: boolean | null;
	},
>(memberships: readonly T[]): T[] {
	return selectCanViewOrganizationDetailsMemberships(
		selectActiveOrganizationUserMemberships(memberships),
	);
}

/**
 * Organization id for org-admin / Switch App entry.
 * Uses an Active membership with canViewOrganizationDetails from user-info
 * (the API already evaluated ViewOrganizationInfo per org). Prefer that flag
 * over a /valid-actions re-check: valid-actions with entityType null only
 * evaluates the session organization and ignores entityId.
 */
export function selectOrgAdminAccessOrganizationId<
	T extends {
		status?: { code?: number | null } | null;
		canViewOrganizationDetails?: boolean | null;
		organization?: { id?: string | null } | null;
	},
>(memberships: readonly T[]): string | null {
	const viewable =
		selectActiveCanViewOrganizationDetailsMemberships(memberships);
	const id = viewable[0]?.organization?.id;
	return typeof id === "string" && id.length > 0 ? id : null;
}

type SwitcherOrganizationSource = {
	id: string;
	organizationId?: string;
	name?: string | null;
	mfaEnabled?: boolean | null;
	createdBy?: string | null;
	createdAt?: string | null;
	createdAtOffset?: string | null;
	updatedBy?: string | null;
	updatedAt?: string | null;
	updatedAtOffset?: string | null;
	totalUserCount?: number;
	statusCode?: number;
	oAuthProviderId?: string | null;
	oAuthProvider?: Organization["oAuthProvider"];
	status?: Organization["status"];
};

/** Normalizes a user-info organization into the admin switcher organization shape. */
export function toAdminSwitcherOrganizations(
	organizations: readonly SwitcherOrganizationSource[],
): Organization[] {
	return organizations.map((org) => ({
		id: org.id,
		organizationId: org.organizationId ?? org.id,
		name: org.name ?? null,
		mfaEnabled: org.mfaEnabled ?? null,
		createdBy: org.createdBy ?? null,
		createdAt: org.createdAt ?? null,
		createdAtOffset: org.createdAtOffset ?? null,
		updatedBy: org.updatedBy ?? null,
		updatedAt: org.updatedAt ?? null,
		updatedAtOffset: org.updatedAtOffset ?? null,
		totalUserCount: org.totalUserCount ?? 0,
		statusCode: org.statusCode ?? OrganizationStatusCode.Created,
		oAuthProviderId: org.oAuthProviderId ?? null,
		oAuthProvider: org.oAuthProvider ?? null,
		status: org.status ?? {
			code: OrganizationStatusCode.Created,
			name: "Created",
			description: "",
		},
	}));
}

/**
 * Resolves the org list for the web shell. Platform admins fall back to
 * membership orgs while the full org list is still loading.
 *
 * Non-platform-admin (membership) switchers only include Active orgs.
 */
export function resolveWebAvailableOrganizations<
	T extends { statusCode?: number },
>(input: ResolveWebAvailableOrganizationsInput<T>): T[] {
	if (input.isPlatformAdmin) {
		return input.allOrgs ?? input.membershipOrganizations;
	}
	return selectActiveMembershipOrganizations(input.membershipOrganizations);
}
